// ABOUTME: Reports an open cart/wishlist panel's scroll position upstream over the agent WebSocket.
// ABOUTME: Owns the `panel_scroll` wire contract and the per-panel dedupe; returns one stable callback.

import { useCallback, useRef } from 'react';

/**
 * Why this exists (client feedback, 2026-09-01).
 *
 * The agent's `scroll_panel` tool moves the open concertina panel, but the agent had no idea where
 * that panel already was — so a guest who had already dragged the bag list to the bottom and then
 * said "scroll down" got a silent no-op instead of "you're already at the end of your bag". Only the
 * browser knows the answer (it depends on rendered heights, not on item count), so the frontend has
 * to volunteer it.
 *
 * **The wire contract, fixed with the backend:**
 *
 * ```json
 * { "type": "panel_scroll", "panel": "CART", "scrollable": true, "at_top": true, "at_bottom": false }
 * ```
 *
 * - `panel` is the panel KIND the rail already uses — `"CART"` or `"WISHLIST"`.
 * - `scrollable` is false when the whole list fits with no overflow at all; in that case both
 *   `at_top` and `at_bottom` are true, which is the honest answer to "can I scroll either way".
 * - Nothing is sent while a panel is shut. The backend already learns that from the existing
 *   `{ type: 'interaction', action: 'closed cart panel' }` message App.js emits off the `overlay`
 *   transition, and a closed panel has no scroll position worth reporting.
 *
 * **Dedupe is the whole reason this is a hook rather than three inline `ws.send` calls.** The panels
 * report from `readScrollState`, which is also their `onScroll` handler — a guest flicking a list on
 * a touchscreen fires that once per frame. Only a state that actually *differs* from the last one
 * sent for that panel goes out; everything else is dropped. The last-sent record is per panel (a
 * Map keyed by kind), because cart and wishlist are independent lists that both mount the same
 * component and would otherwise mask each other's transitions.
 *
 * A send that could not happen (socket missing, or not yet OPEN) deliberately does NOT update the
 * record, so the next call re-attempts rather than treating an undelivered state as delivered.
 *
 * **Two ways the cache MUST be dropped, or the dedupe silently re-creates the bug it was added to
 * fix (review, 2026-09-01).** This hook is mounted for the whole App lifetime while the panels come
 * and go under it, and the backend does not keep what it is told forever:
 *
 * 1. *Panel close.* `set_panel_open(..., False)` on the backend POPS that panel's scroll entry
 *    (`services/shopping_service.py`), and App.js sends `closed cart panel` on every collapse. So
 *    after open → close → reopen the backend knows nothing while a long-lived cache still holds the
 *    identical state (a remounted grid always starts at the top, which is usually exactly what was
 *    last reported) — the report is suppressed, "scroll down" hits an unknown state, and the agent
 *    claims to have scrolled a list that never moved. `{ panel, forget: true }` from the panel's own
 *    unmount cleanup drops that one panel's entry, so the next open always re-reports. It is
 *    per-panel on purpose: a CART↔WISHLIST switch is a close plus an open, and clearing the whole
 *    map there would throw away the incoming panel's fresh state along with the outgoing one's.
 * 2. *Reconnect.* The backend's scroll state lives in an in-process dict with no Redis backing, so a
 *    SPEC-11 reconnect onto a fresh replica loses all of it. The cache is therefore keyed to the
 *    WebSocket INSTANCE — App.js assigns a brand-new `WebSocket` to `ws.current` on every reconnect
 *    attempt — and a different instance wipes it wholesale, which is correct here because *every*
 *    panel's state is gone on the far side.
 *
 * @param {{current: WebSocket|null}|((payload: object) => void)} socket the App-level `ws` ref, or a
 *   plain send function (used by the unit test, and by any caller that already owns the guard).
 * @returns {(state: {panel: string, scrollable?: boolean, atTop?: boolean, atBottom?: boolean,
 *   forget?: boolean}) => void} a referentially stable callback, safe to pass straight down as
 *   `onScrollStateChange`. `forget: true` drops that panel's cached state and sends nothing.
 */
export default function usePanelScrollReporter(socket) {
  // A ref, not state: nothing here should ever cause a render, and the panels call this from inside
  // an event handler where a state update would be a wasted commit per scroll frame.
  const lastSentRef = useRef(new Map());
  // Mirror of the socket argument, so the returned callback can stay stable across renders even
  // though App.js's `ws` ref identity is only stable by convention.
  const socketRef = useRef(socket);
  socketRef.current = socket;
  // Which socket the cached states were reported TO — see note 2 above.
  const cachedForRef = useRef(null);

  return useCallback((state) => {
    const panel = state?.panel;
    if (!panel) return;

    // A panel saying goodbye. No message: the backend learns the panel is shut from the existing
    // `closed <panel> panel` interaction, and this only has to make sure the NEXT open is not
    // mistaken for a repeat of the last one.
    if (state.forget) {
      lastSentRef.current.delete(panel);
      return;
    }

    // A list with no overflow is "at" both ends at once — see the contract note above. Normalised
    // here rather than at the four call sites so the dedupe key can never see two spellings of the
    // same state (e.g. {scrollable:false, at_top:false} vs {scrollable:false, at_top:true}).
    const scrollable = !!state.scrollable;
    const payload = {
      type: 'panel_scroll',
      panel,
      scrollable,
      at_top: scrollable ? !!state.atTop : true,
      at_bottom: scrollable ? !!state.atBottom : true,
    };

    const target = socketRef.current;
    // A different socket object than the one the cache was built against means a reconnect has
    // happened and the backend's in-process scroll state went with the old replica — see note 2.
    // Checked BEFORE the dedupe, so the very first report after a reconnect always goes out.
    const identity = typeof target === 'function' ? target : target?.current ?? null;
    if (cachedForRef.current !== identity) {
      lastSentRef.current.clear();
      cachedForRef.current = identity;
    }

    const key = `${payload.scrollable}|${payload.at_top}|${payload.at_bottom}`;
    if (lastSentRef.current.get(panel) === key) return;

    if (typeof target === 'function') {
      target(payload);
    } else {
      // Same guard as every other send in App.js — a CONNECTING or CLOSED socket throws on send.
      const ws = target?.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify(payload));
    }

    lastSentRef.current.set(panel, key);
  }, []);
}
